import { SystemEntity } from "./systemEntities";

/**
 * Configurable task-queue columns that read a nested value off a task row.
 *
 * A column's `apiField` is `<source>.<dotted.path>` (e.g. `input.supplier.name`,
 * `outcomeData.reason`, `anchor.invoiceNumber`). A plain `apiField` without a
 * known source prefix keeps the legacy behavior (top-level TaskResponse key,
 * else `metadata[apiField]`).
 */
export const TASK_COLUMN_SOURCES = [
  "input",
  "correction",
  "outcomeData",
  "metadata",
  "anchor",
] as const;

export type TaskColumnSource = (typeof TASK_COLUMN_SOURCES)[number];

export interface TaskColumnPath {
  source: TaskColumnSource;
  /** Non-empty list of path segments below the source. */
  segments: string[];
}

function isTaskColumnSource(value: string): value is TaskColumnSource {
  return (TASK_COLUMN_SOURCES as readonly string[]).includes(value);
}

/** Split a dotted path into non-empty segments (`a..b` → `["a", "b"]`). */
export function splitColumnPath(path: string): string[] {
  return path
    .split(".")
    .map((segment) => segment.trim())
    .filter(Boolean);
}

/**
 * Parse `<source>.<path>`; returns null for plain (legacy) apiFields or a
 * bare source with no path.
 */
export function parseTaskColumnPath(apiField: string): TaskColumnPath | null {
  const dot = apiField.indexOf(".");
  if (dot <= 0) return null;
  const source = apiField.slice(0, dot);
  if (!isTaskColumnSource(source)) return null;
  const segments = splitColumnPath(apiField.slice(dot + 1));
  return segments.length > 0 ? { source, segments } : null;
}

export function buildTaskColumnApiField(
  source: TaskColumnSource,
  path: string,
): string {
  return `${source}.${splitColumnPath(path).join(".")}`;
}

function isIndexable(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * Walk `segments` into a JSON-ish value. Arrays accept numeric segments.
 * Own properties only, so `constructor`/`__proto__` never resolve.
 */
export function getValueAtPath(root: unknown, segments: string[]): unknown {
  let current: unknown = root;
  for (const segment of segments) {
    if (
      !isIndexable(current) ||
      !Object.prototype.hasOwnProperty.call(current, segment)
    ) {
      return undefined;
    }
    current = current[segment];
  }
  return current;
}

/** Max anchor paths one `tasks.list` call may request. */
export const MAX_TASK_ANCHOR_FIELDS = 25;

/**
 * Anchor-field blocklist. Every scalar/JSON column on an anchor entity's model
 * is selectable for task-page columns EXCEPT these. A path is blocked when ANY
 * of its segments matches, so `originMetadata.accessToken` is blocked too.
 *
 * Global patterns cover credentials, secrets, and banking/identity numbers;
 * per-entity names cover raw or bulky payloads (email bodies, headers) that
 * belong on the entity's own page, not in a table cell.
 */
const BLOCKED_SEGMENT_PATTERNS: readonly RegExp[] = [
  /pass(word|wd|phrase)/i,
  /secret/i,
  /token/i,
  /api_?key/i,
  /private_?key/i,
  /credential/i,
  /encrypt/i,
  /cipher/i,
  /(hash|salt)$/i,
  /^(otp|mfa|totp)/i,
  /ssn|social_?security/i,
  /tax_?id|^tin$|^ein$/i,
  /account_?number|routing_?number|^iban$|^swift|^bic$/i,
  /card_?number|^cvv|^cvc|^pan$/i,
  /^raw/i,
  /^(cookie|authorization|session)/i,
];

const ENTITY_BLOCKED_FIELDS: Partial<Record<SystemEntity, readonly string[]>> =
  {
    [SystemEntity.INBOUND_EMAIL]: [
      "textContent",
      "htmlContent",
      "headers",
      "envelope",
      "charsets",
      "spamReport",
      "senderIp",
      "dkim",
      "spf",
      "recipientSignals",
    ],
    [SystemEntity.OUTBOUND_EMAIL]: [
      "content",
      "htmlContent",
      "aiOriginalContent",
      "aiOriginalHtmlContent",
      "idempotencyKey",
    ],
    [SystemEntity.AR_CASH_RECEIPT]: ["plaidTransactionId"],
  };

export function isBlockedAnchorPath(
  entityType: SystemEntity,
  segments: readonly string[],
): boolean {
  if (segments.length === 0) return true;
  const [root] = segments;
  if (root && ENTITY_BLOCKED_FIELDS[entityType]?.includes(root)) return true;
  return segments.some((segment) =>
    BLOCKED_SEGMENT_PATTERNS.some((pattern) => pattern.test(segment)),
  );
}

/**
 * Field(s) the anchor badge shows as its label, in priority order. Entities
 * without an entry render the generic type label + short id.
 */
export const ANCHOR_LABEL_FIELDS: Partial<
  Record<SystemEntity, readonly string[]>
> = {
  [SystemEntity.AP_INVOICE]: ["invoiceNumber"],
  [SystemEntity.AP_SUPPLIER]: ["name"],
  [SystemEntity.AR_CUSTOMER]: ["name"],
  [SystemEntity.AR_CASH_RECEIPT]: ["customerName", "merchantName"],
  // Famous order number (SONO) on both.
  [SystemEntity.AR_SALES_ORDER]: ["originId"],
  [SystemEntity.AR_INVOICE]: ["invoiceNumber"],
  [SystemEntity.INBOUND_EMAIL]: ["subject", "from"],
  [SystemEntity.OUTBOUND_EMAIL]: ["subject"],
  [SystemEntity.IM_PRICELIST_CASE]: ["vendorCode"],
};
